import type {
  CriarTratamentoTemplateDtoEstrutura,
  CriarTratamentoTemplateDtoEstruturaFarmacosItem,
  CriarTratamentoTemplateDtoEstruturaFarmacosItemFrequencia,
  CriarTratamentoTemplateDtoEstruturaFarmacosItemVia,
  TratamentoTemplateResponse,
} from '@/api/generated/model';
import { linhaVazia, type LinhaDeMedicamento } from '@/features/historico/prontuarioForm';
import { numeroParaCampo, paraNumero } from '@/features/historico/tratamento';

/**
 * MODELO DE TRATAMENTO — a tradução entre o editor e o que fica salvo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ **O modelo guarda ESTRUTURA, não valor.** Quais fármacos, por que via, em
 *  que frequência — e a dose só como sugestão inicial.
 *
 *  É a mesma regra que o editor de catálogo de exames já segue, e existe pelo
 *  mesmo motivo: um modelo que preenche dose sozinho vira dose errada assinada
 *  por alguém que só clicou em "aplicar". A unidade (`mg`, `mL`, `UI`) decorre
 *  da apresentação do fármaco e é estrutura; o NÚMERO decorre do peso do animal
 *  e da consulta, e é do clínico.
 *
 *  Por isso `doseUnidade` é obrigatória no modelo e `doseSugerida` não. Modelo
 *  sem dose é um modelo COMPLETO, e o editor abre aquele campo vazio de
 *  propósito.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** O que o modelo consegue guardar de uma linha do editor. */
function farmacoDoModelo(
  linha: LinhaDeMedicamento,
): CriarTratamentoTemplateDtoEstruturaFarmacosItem | null {
  // Sem identidade de fármaco não há o que guardar — a linha em branco que o
  // usuário acrescentou e não preencheu simplesmente não entra no modelo.
  const catalogoFarmacoId = linha.catalogoFarmacoId?.trim() || null;
  const nomeLivre = linha.nomeLivre?.trim() || null;
  if (!catalogoFarmacoId && !nomeLivre) return null;

  const doseUnidade = linha.doseUnidade?.trim();
  if (!doseUnidade) return null;

  const frequencia = linha.frequencia as CriarTratamentoTemplateDtoEstruturaFarmacosItemFrequencia;
  const via = linha.via?.trim();

  return {
    catalogoFarmacoId,
    nomeLivre: catalogoFarmacoId ? null : nomeLivre,
    doseUnidade,
    frequencia,
    frequenciaLivre: frequencia === 'OUTRA' ? linha.frequenciaLivre?.trim() || null : null,
    via: (via || null) as CriarTratamentoTemplateDtoEstruturaFarmacosItemVia,
    // Sugestões, e o nome do campo diz isso. `''` vira `null`: um modelo sem
    // dose é melhor que um modelo com a dose do último paciente.
    doseSugerida: paraNumeroOuNulo(linha.doseValor),
    dosePorKgSugerida: paraNumeroOuNulo(linha.dosePorKg),
    observacao: null,
  };
}

function paraNumeroOuNulo(valor: string | undefined): number | null {
  const texto = valor?.trim();
  if (!texto) return null;

  const numero = paraNumero(texto);
  return Number.isFinite(numero) && numero > 0 ? numero : null;
}

/**
 * As linhas do editor viram a `estrutura` do modelo.
 *
 * Devolve `null` quando nenhuma linha tem fármaco e unidade — não há modelo a
 * salvar, e quem chama precisa DIZER isso em vez de gravar um modelo vazio que
 * ninguém consegue usar depois.
 */
export function estruturaDoEditor(
  linhas: readonly LinhaDeMedicamento[],
): CriarTratamentoTemplateDtoEstrutura | null {
  const farmacos = linhas
    .map(farmacoDoModelo)
    .filter((f): f is CriarTratamentoTemplateDtoEstruturaFarmacosItem => f !== null);

  return farmacos.length === 0 ? null : { farmacos };
}

/**
 * O modelo vira linhas do editor.
 *
 * ⚠️ **Sem `medicamentoId`.** Ele é o id do `TratamentoMedicamento` que a linha
 * continua, e um modelo não continua nada — aplicar um modelo acrescenta
 * fármacos NOVOS. Herdá-lo de outro tratamento faria o backend achar que a
 * linha é a continuação de um medicamento que pertence a outro paciente.
 *
 * ⚠️ **Sem `apresentacaoId`.** A apresentação é a marca com a concentração, e
 * ela pertence ao caso: o modelo diz "trilostano por via oral, SID", não "o
 * Vetoryl de 30 mg". O campo abre vazio para o clínico escolher.
 */
export function linhasDoModelo(modelo: TratamentoTemplateResponse): LinhaDeMedicamento[] {
  const farmacos = modelo.estrutura?.farmacos ?? [];

  return farmacos.map((farmaco) => ({
    ...linhaVazia(),
    catalogoFarmacoId: farmaco.catalogoFarmacoId ?? '',
    nomeLivre: farmaco.catalogoFarmacoId ? '' : (farmaco.nomeLivre ?? ''),
    nome: farmaco.nome ?? farmaco.nomeLivre ?? '',
    doseUnidade: farmaco.doseUnidade ?? '',
    doseValor: numeroParaCampo(farmaco.doseSugerida ?? null),
    dosePorKg: numeroParaCampo(farmaco.dosePorKgSugerida ?? null),
    frequencia: farmaco.frequencia ?? 'SID',
    frequenciaLivre: farmaco.frequenciaLivre ?? '',
    via: farmaco.via ?? '',
  }));
}

/**
 * Quantos fármacos do editor cabem num modelo.
 *
 * A tela usa para decidir se o botão *Salvar como modelo* fica habilitado, e o
 * número entra na confirmação — "2 de 3 fármacos entram no modelo" é o aviso
 * que impede alguém de salvar achando que levou tudo.
 */
export function quantosEntramNoModelo(linhas: readonly LinhaDeMedicamento[]): number {
  return linhas.filter((linha) => farmacoDoModelo(linha) !== null).length;
}
